import { createApi } from '@reduxjs/toolkit/query/react';

import {
  buildCategoryIdByCode,
  flattenBoosterDraft,
  mapPlanBlock,
  mapQuestion,
  mapReportFacts,
  mapReportSchool,
  mapScoreCategories,
  mapTestDates,
  restoreDraftFromSubmission,
  RawBoosterPlan,
  RawBoosterQuestion,
  RawBoosterSubmission,
} from './satActBooster.transform';
import {
  ComputeRequest,
  ComputeResponse,
  CreateSubmissionRequest,
  DailyQuestion,
  DayCompletionRequest,
  FinalizeSubmissionRequest,
  MostRecentSubmission,
  QuestionsForDayParams,
  QuizAttemptAnswersParams,
  QuizAttemptAnswersResponse,
  QuizAttemptRequest,
  RawReportFacts,
  ReportFacts,
  ReportSchool,
  SchoolFitRequest,
  SchoolFitResponse,
  ScoreCategoryRef,
  SubmissionResponse,
  TargetTest,
  TestDate,
} from './satActBooster.types';
import { baseQueryWithAuth } from '../baseQuery';
import { profileApi } from '../profile/profile';

type RawSubmissionResponse = Readonly<{ id: string }>;
/** One answer row as the attempt-answers read returns it, embedding its question in raw (snake_case) shape. */
type RawQuizAttemptAnswer = Readonly<{
  questionOrder: number;
  questionId: string;
  chosenOptionIndex: number | null;
  isCorrect: boolean;
  question: RawBoosterQuestion | null;
}>;
type RawQuizAttemptAnswersResponse = Readonly<{
  score: number;
  total: number;
  scoreCategoryId: number;
  answers: readonly RawQuizAttemptAnswer[];
}>;
type RawMostRecentResponse = Readonly<{
  submission: RawBoosterSubmission;
  reportFacts: RawReportFacts;
  /** The study-plan block joined by the calendar engine; absent for a partial submission. */
  plan?: RawBoosterPlan | null;
}>;

const mapMostRecentResponse = (response: RawMostRecentResponse): MostRecentSubmission => ({
  id: response.submission.id,
  completedAt: response.submission.completed_at ?? null,
  reportContent: response.submission.report_content ?? null,
  reportFacts: mapReportFacts(response.reportFacts),
  plan: mapPlanBlock(response.submission, response.plan),
  restoredDraft: restoreDraftFromSubmission(response.submission),
});

/** Test Booster data layer; the draft is flattened to the submission shape in the transform module. */
export const satActBoosterApi = createApi({
  reducerPath: 'satActBoosterApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['BoosterSubmission'],
  endpoints: (build) => ({
    compute: build.mutation<ReportFacts, ComputeRequest>({
      query: ({ draft }) => ({
        url: 'sat-act-booster/compute',
        method: 'POST',
        body: flattenBoosterDraft(draft),
      }),
      transformResponse: (response: ComputeResponse) => mapReportFacts(response.reportFacts),
    }),
    createSubmission: build.mutation<SubmissionResponse, CreateSubmissionRequest>({
      query: ({ draft, categoryIdByCode, reportContent }) => ({
        url: 'sat-act-booster/submission',
        method: 'POST',
        body: flattenBoosterDraft(draft, { categoryIdByCode, reportContent }),
      }),
      transformResponse: (response: RawSubmissionResponse) => ({ id: response.id }),
      invalidatesTags: ['BoosterSubmission'],
      // Saving a submission back-fills whichever profile blanks the intake answered, so the
      // cached profile no longer matches the server.
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          // A rejected save may still have landed; nothing is refreshed here.
          return;
        }

        dispatch(profileApi.util.invalidateTags(['UserProfile']));
      },
    }),
    finalizeSubmission: build.mutation<SubmissionResponse, FinalizeSubmissionRequest>({
      query: ({ id, draft, categoryIdByCode, reportContent }) => ({
        url: `sat-act-booster/submission/${id}/complete`,
        method: 'POST',
        body: flattenBoosterDraft(draft, { categoryIdByCode, reportContent }),
      }),
      transformResponse: (response: RawSubmissionResponse) => ({ id: response.id }),
      invalidatesTags: ['BoosterSubmission'],
    }),
    // The newest submission of any state — an unfinished run included, which is the point: this is
    // what a resumable run is read from. Anything showing a plan wants getMostRecentPlan instead.
    getMostRecent: build.query<MostRecentSubmission, undefined>({
      query: () => 'sat-act-booster/submission/most-recent',
      transformResponse: mapMostRecentResponse,
      providesTags: ['BoosterSubmission'],
    }),
    // The newest *finished* submission, so a run abandoned afterwards can't hide the plan.
    getMostRecentPlan: build.query<MostRecentSubmission, undefined>({
      query: () => 'sat-act-booster/submission/most-recent?status=completed',
      transformResponse: mapMostRecentResponse,
      providesTags: ['BoosterSubmission'],
    }),
    getTestDates: build.query<TestDate[], TargetTest>({
      query: (test) => `sat-act-booster/test-dates?test=${test}`,
      transformResponse: mapTestDates,
    }),
    getScoreCategories: build.query<ScoreCategoryRef[], TargetTest>({
      query: (test) => `sat-act-booster/score-categories?test=${test}`,
      transformResponse: mapScoreCategories,
    }),
    getSchoolFit: build.query<readonly ReportSchool[], SchoolFitRequest>({
      query: ({ test, currentScore, institutionIds }) => {
        const params = new URLSearchParams({ test, institutionIds: institutionIds.join(',') });

        if (currentScore !== null) params.set('currentScore', String(currentScore));

        return `sat-act-booster/school-fit?${params.toString()}`;
      },
      transformResponse: (response: SchoolFitResponse) => response.schools.map(mapReportSchool),
    }),
    // The study day's fixed five, selected server-side; they render in the returned order.
    getQuestionsForDay: build.query<DailyQuestion[], QuestionsForDayParams>({
      query: ({ submissionId, dayDate }) =>
        `sat-act-booster/questions/for-day?test_booster_submission_id=${submissionId}&day_date=${dayDate}`,
      transformResponse: (rows: RawBoosterQuestion[]) => rows.map(mapQuestion),
    }),
    getQuizAttemptAnswers: build.query<QuizAttemptAnswersResponse, QuizAttemptAnswersParams>({
      query: ({ submissionId, dayDate }) =>
        `sat-act-booster/quiz-attempt/answers?test_booster_submission_id=${submissionId}&day_date=${dayDate}`,
      // Embed each answer's question (mapped from the raw row) so the results view renders self-contained.
      transformResponse: (response: RawQuizAttemptAnswersResponse): QuizAttemptAnswersResponse => ({
        score: response.score,
        total: response.total,
        scoreCategoryId: response.scoreCategoryId,
        answers: response.answers.map((answer) => ({
          questionOrder: answer.questionOrder,
          questionId: answer.questionId,
          chosenOptionIndex: answer.chosenOptionIndex,
          isCorrect: answer.isCorrect,
          question: answer.question ? mapQuestion(answer.question) : null,
        })),
      }),
    }),
    recordQuizAttempt: build.mutation<{ id: string }, QuizAttemptRequest>({
      // score/total are not sent — the server grades the answers and derives them. They ride the
      // mutation arg only to feed the optimistic day-attempt overlay below.
      query: ({ submissionId, dayDate, scoreCategoryId, answers }) => ({
        url: 'sat-act-booster/quiz-attempt',
        method: 'POST',
        body: {
          test_booster_submission_id: submissionId,
          day_date: dayDate,
          score_category_id: scoreCategoryId,
          answers: answers.map((answer) => ({
            question_order: answer.questionOrder,
            question_id: answer.questionId,
            chosen_option_id: answer.chosenOptionId,
          })),
        },
      }),
      transformResponse: (response: { id: string }) => ({ id: response.id }),
      // The optimistic score is the client's own tally; refetching replaces it with the server's grade.
      invalidatesTags: ['BoosterSubmission'],
      // Surface the recorded attempt on the day immediately so a return visit (and the calendar)
      // reflect it without a refetch; roll back if the save fails.
      async onQueryStarted({ submissionId, dayDate, score, total }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          satActBoosterApi.util.updateQueryData('getMostRecentPlan', undefined, (draft) => {
            const day = draft.plan?.days.find((planDay) => planDay.date === dayDate);

            if (day)
              (day as { attempt: { score: number; total: number } }).attempt = { score, total };
          }),
        );

        try {
          await queryFulfilled;
        } catch (error) {
          patch.undo();
          console.error('Failed to record quiz attempt:', submissionId, dayDate, error);
        }
      },
    }),
    setDayCompletion: build.mutation<{ completed: boolean }, DayCompletionRequest>({
      query: ({ submissionId, dayDate, completed }) => ({
        url: 'sat-act-booster/day-completion',
        method: 'PUT',
        body: {
          test_booster_submission_id: submissionId,
          day_date: dayDate,
          completed,
        },
      }),
      transformResponse: (response: { completed: boolean }) => ({ completed: response.completed }),
      // A refetch triggered by a sibling mutation replaces the whole cache entry, so the patch
      // below can't be the only record of the toggle — invalidate so the server settles it.
      invalidatesTags: ['BoosterSubmission'],
      // Reflect the toggle immediately on the day header and calendar; roll back if the save fails.
      async onQueryStarted({ submissionId, dayDate, completed }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          satActBoosterApi.util.updateQueryData('getMostRecentPlan', undefined, (draft) => {
            const day = draft.plan?.days.find((planDay) => planDay.date === dayDate);

            if (day) (day as { completed: boolean }).completed = completed;
          }),
        );

        try {
          await queryFulfilled;
        } catch (error) {
          patch.undo();
          console.error('Failed to set day completion:', submissionId, dayDate, error);
        }
      },
    }),
  }),
});

export { buildCategoryIdByCode };

export const {
  useComputeMutation,
  useCreateSubmissionMutation,
  useFinalizeSubmissionMutation,
  useGetMostRecentQuery,
  useGetMostRecentPlanQuery,
  useGetTestDatesQuery,
  useGetScoreCategoriesQuery,
  useGetSchoolFitQuery,
  useGetQuestionsForDayQuery,
  useGetQuizAttemptAnswersQuery,
  useRecordQuizAttemptMutation,
  useSetDayCompletionMutation,
} = satActBoosterApi;
